import { useCallback, useEffect, useRef, useState } from 'react';
import { matchExpectedLineResults } from '../core/automaticRehearsal.js';
import { getSpeechRecognitionSupport } from '../modules/consent-gated-speech-recognition-session-kit/index.js';

const INITIAL = { enabled: false, status: 'off', mode: 'none', lastScore: 0, lastError: '', lastDiagnostic: null, activeCueId: '' };

function recognitionConstructor() {
  return window.SpeechRecognition || window.webkitSpeechRecognition || null;
}

export function friendlyRecognitionError(code, { isSecureContext = true, fallback = 'timer' } = {}) {
  const fallbackText = fallback === 'timer'
    ? 'Automatic speaking time will advance the scene; no director tap is needed.'
    : 'You can keep practicing with the on-screen controls.';
  if (code === 'not-allowed') {
    if (!isSecureContext) return `This phone browser blocks microphone access on the local room link. ${fallbackText}`;
    return `Microphone access is blocked for this site. Allow it in the browser’s site settings, then tap Try microphone again. ${fallbackText}`;
  }
  if (code === 'service-not-allowed') return `The phone’s speech recognition service is unavailable or disabled. Microphone permission alone cannot enable that service. ${fallbackText}`;
  if (code === 'stop-timeout') return `The speech service started but did not finish microphone setup. QLine stopped the test. Repeated permission prompts do not confirm line detection. ${fallbackText}`;
  if (code === 'audio-capture') return `This phone could not open its microphone. ${fallbackText}`;
  if (code === 'network') return `The phone’s speech service is unavailable. ${fallbackText}`;
  if (code === 'unsupported') return `This browser does not offer line detection. ${fallbackText}`;
  if (code === 'timeout') return `Microphone setup took too long. ${fallbackText}`;
  return `Automatic line detection could not start. ${fallbackText}`;
}

export function useExpectedLineRecognition({ language = 'en-US', fallback = 'timer' } = {}) {
  const [state, setState] = useState(() => ({ ...INITIAL, supported: getSpeechRecognitionSupport(window).supported }));
  const recognitionRef = useRef(null);
  const targetRef = useRef(null);
  const enabledRef = useRef(false);
  const modeRef = useRef('none');
  const restartRef = useRef(null);
  const permissionRef = useRef(null);
  const mountedRef = useRef(true);
  const listenerEpochRef = useRef(0);
  const transcriptRef = useRef(null);

  const cancelPermission = useCallback(() => {
    permissionRef.current?.cancel();
  }, []);

  const clearRestart = useCallback(() => {
    if (restartRef.current) window.clearTimeout(restartRef.current);
    restartRef.current = null;
  }, []);

  const abortActive = useCallback(() => {
    listenerEpochRef.current++;
    clearRestart();
    const recognition = recognitionRef.current;
    recognitionRef.current = null;
    if (recognition) {
      recognition.onstart = null;
      recognition.onspeechstart = null;
      recognition.onresult = null;
      recognition.onerror = null;
      recognition.onend = null;
      try { recognition.abort(); } catch {}
    }
  }, [clearRestart]);

  const enterFallback = useCallback((message) => {
    abortActive();
    enabledRef.current = false;
    modeRef.current = 'none';
    targetRef.current = null;
    transcriptRef.current = null;
    recognitionRef.current = null;
    setState((current) => ({ ...current, enabled: false, status: 'timed-fallback', mode: 'none', activeCueId: '', lastError: message }));
  }, [abortActive]);

  const startTarget = useCallback((target) => {
    const Recognition = recognitionConstructor();
    if (!Recognition || !enabledRef.current || !target?.expectedText || document.visibilityState === 'hidden') return false;
    abortActive();
    targetRef.current = target;
    if (transcriptRef.current?.target !== target) transcriptRef.current = { target, finalized: [], startedAt: Date.now() };
    const transcript = transcriptRef.current;
    const priorFinals = transcript.finalized;
    let currentFinals = [];
    const listenerEpoch = listenerEpochRef.current;
    const recognition = new Recognition();
    recognition.lang = language;
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.maxAlternatives = 3;
    if ('processLocally' in recognition) recognition.processLocally = modeRef.current === 'on-device';
    recognitionRef.current = recognition;
    const startedAt = transcript.startedAt;
    let matched = false;
    let speechStarted = false;
    const ownsTarget = () => mountedRef.current && listenerEpochRef.current === listenerEpoch && recognitionRef.current === recognition && targetRef.current === target;
    recognition.onstart = () => { if (!ownsTarget()) return; setState((current) => ({ ...current, status: 'listening', activeCueId: target.cueId, lastError: '' })); };
    recognition.onspeechstart = () => {
      if (!ownsTarget() || speechStarted) return;
      speechStarted = true;
      setState((current) => ({ ...current, status: 'hearing-speech', activeCueId: target.cueId, lastError: '' }));
      target.onSpeechStart?.({ cueId: target.cueId, elapsedMs: Date.now() - startedAt });
    };
    recognition.onresult = (event) => {
      if (!ownsTarget()) return;
      const elapsedMs = Date.now() - startedAt;
      currentFinals = Array.from(event.results || []).filter(result => result?.isFinal === true).slice(-64).map(result => Object.assign(
        Array.from(result).slice(0, 3).map(alternative => ({ transcript: String(alternative?.transcript || '').slice(0, 4000), confidence: alternative?.confidence })),
        { isFinal: true },
      ));
      const best = matchExpectedLineResults(target.expectedText, [...priorFinals, ...Array.from(event.results || [])].slice(-64), { elapsedMs });
      setState((current) => ({ ...current, lastScore: best.score, status: best.matched ? 'matched' : best.final ? 'listening' : 'hearing-speech' }));
      if (best.matched && !matched) {
        matched = true;
        target.onMatch?.({ cueId: target.cueId, score: best.score, final: true, elapsedMs });
        targetRef.current = null;
        transcriptRef.current = null;
        try { recognition.stop(); } catch {}
      }
    };
    recognition.onerror = (event) => {
      if (!ownsTarget()) return;
      if (event.error === 'aborted' || event.error === 'no-speech') return;
      enterFallback(friendlyRecognitionError(event.error, { isSecureContext: window.isSecureContext !== false, fallback }));
    };
    recognition.onend = () => {
      if (!mountedRef.current || recognitionRef.current !== recognition) return;
      recognitionRef.current = null;
      const currentTarget = targetRef.current;
      if (!matched && enabledRef.current && currentTarget?.cueId === target.cueId && document.visibilityState !== 'hidden') {
        if (transcriptRef.current === transcript) transcript.finalized = [...priorFinals, ...currentFinals].slice(-64);
        restartRef.current = window.setTimeout(() => { if (mountedRef.current && listenerEpochRef.current === listenerEpoch && !recognitionRef.current && targetRef.current === currentTarget && enabledRef.current) startTarget(currentTarget); }, 450);
      } else if (!matched) setState((current) => ({ ...current, status: enabledRef.current ? 'ready' : current.lastError ? 'timed-fallback' : 'off', activeCueId: '' }));
    };
    try {
      recognition.start();
      setState((current) => ({ ...current, status: 'starting', activeCueId: target.cueId, lastError: '' }));
      return true;
    } catch {
      enterFallback(friendlyRecognitionError('start-failed', { isSecureContext: window.isSecureContext !== false, fallback }));
      return false;
    }
  }, [abortActive, enterFallback, fallback, language]);

  const enable = useCallback(async () => {
    if (!mountedRef.current) return { ready: false, mode: 'none' };
    cancelPermission();
    const Recognition = recognitionConstructor();
    if (!Recognition) {
      setState((current) => ({ ...current, enabled: false, supported: false, status: 'unsupported', mode: 'none', lastError: friendlyRecognitionError('unsupported', { isSecureContext: window.isSecureContext !== false, fallback }) }));
      return { ready: false, mode: 'unsupported' };
    }
    setState((current) => ({ ...current, status: 'requesting-permission', lastError: '', lastDiagnostic: null }));
    return new Promise((resolve) => {
      const operation = {};
      permissionRef.current = operation;
      let probe, availabilityTimeout, timeout, deadlineId = 0;
      let mode = 'browser-managed', started = false, settled = false, phase = 'availability';
      const began = Date.now(), events = [];
      const record = name => { if (events.length < 12) events.push(name); };
      const diagnostic = code => ({ code: ['not-allowed','service-not-allowed','audio-capture','network','no-speech','aborted','unsupported','timeout','stop-timeout','start-failed','language-not-supported'].includes(code) ? code : 'other', phase, events: events.join(' > '), elapsedMs: Math.max(0, Math.min(60000, Date.now() - began)) });
      const ownsPermission = () => !settled && mountedRef.current && permissionRef.current === operation;
      const finish = (ready, error = '', cancelled = false, code = 'start-failed') => {
        if (settled) return;
        const current = ownsPermission();
        settled = true;
        window.clearTimeout(availabilityTimeout);
        window.clearTimeout(timeout);
        if (probe) {
          probe.onstart = null; probe.onend = null; probe.onerror = null; probe.onaudiostart = null; probe.onaudioend = null;
          if (cancelled || !ready) { try { probe.abort(); } catch {} }
        }
        if (permissionRef.current === operation) permissionRef.current = null;
        ready = current && !cancelled && ready;
        if (current && !cancelled) {
          enabledRef.current = ready;
          modeRef.current = ready ? mode : 'none';
          setState((current) => ({ ...current, enabled: ready, supported: true, status: ready ? 'ready' : 'timed-fallback', mode: ready ? mode : 'none', lastError: error, lastDiagnostic: ready ? null : diagnostic(code) }));
        }
        resolve({ ready, mode: ready ? mode : 'none' });
      };
      operation.cancel = () => finish(false, '', true);
      const startProbe = (availability) => {
        if (!ownsPermission()) return;
        window.clearTimeout(availabilityTimeout);
        if (availability === 'available') mode = 'on-device';
        try {
          probe = new Recognition();
          probe.lang = language;
          probe.continuous = false;
          probe.interimResults = false;
          if (mode === 'on-device' && !('processLocally' in probe)) mode = 'browser-managed';
          if ('processLocally' in probe) probe.processLocally = mode === 'on-device';
          const deadline = (delay, code) => {
            const currentDeadline = ++deadlineId;
            window.clearTimeout(timeout);
            timeout = window.setTimeout(() => {
              if (!ownsPermission() || currentDeadline !== deadlineId) return;
              record('timeout'); finish(false, friendlyRecognitionError(code, { isSecureContext: window.isSecureContext !== false, fallback }), false, code);
            }, delay);
          };
          phase = 'permission-or-start'; record('start-request');
          // Permission can take longer than the service shutdown. Give each
          // phase its own deadline; a late Allow never consumes the stop budget.
          deadline(30000, 'timeout');
          probe.onaudiostart = () => { if (ownsPermission()) record('audio-start'); };
          probe.onaudioend = () => { if (ownsPermission()) record('audio-end'); };
          probe.onstart = () => {
            if (!ownsPermission()) return;
            started = true; record('service-start'); phase = 'stopping-service';
            deadline(10000, 'stop-timeout'); record('stop-request');
            try { probe.stop(); } catch { finish(false, friendlyRecognitionError('start-failed', { fallback }), false, 'start-failed'); }
          };
          probe.onend = () => { if (!ownsPermission()) return; record('service-end'); finish(started, started ? '' : friendlyRecognitionError('start-failed', { isSecureContext: window.isSecureContext !== false, fallback })); };
          probe.onerror = event => {
            if (!ownsPermission()) return;
            record('error');
            // A no-speech event is not proof that the engine released its mic.
            if (started && event.error === 'no-speech') return;
            finish(false, friendlyRecognitionError(event.error, { isSecureContext: window.isSecureContext !== false, fallback }), false, event.error);
          };
          probe.start();
        } catch (error) { const code = error.name === 'NotAllowedError' ? 'not-allowed' : 'start-failed'; record('start-exception'); finish(false, friendlyRecognitionError(code, { isSecureContext: window.isSecureContext !== false, fallback }), false, code); }
      };
      if (typeof Recognition.available === 'function') {
        try {
          Promise.race([
            Recognition.available({ langs: [language], processLocally: true, quality: 'dictation' }),
            new Promise((resolveAvailability) => { availabilityTimeout = window.setTimeout(() => resolveAvailability('unavailable'), 1500); }),
          ]).then(startProbe, () => startProbe('unavailable'));
        } catch { startProbe('unavailable'); }
      } else startProbe('unavailable');
    });
  }, [cancelPermission, fallback, language]);

  const listen = useCallback((target) => { transcriptRef.current = null; return startTarget(target); }, [startTarget]);
  const stop = useCallback(() => {
    cancelPermission();
    targetRef.current = null;
    transcriptRef.current = null;
    abortActive();
    setState((current) => ({ ...current, status: enabledRef.current ? 'ready' : current.lastError ? 'timed-fallback' : 'off', activeCueId: '' }));
  }, [abortActive, cancelPermission]);
  const disable = useCallback(() => {
    enabledRef.current = false;
    modeRef.current = 'none';
    stop();
    setState((current) => ({ ...current, enabled: false, status: 'off', mode: 'none', activeCueId: '', lastError: '', lastDiagnostic: null }));
  }, [stop]);

  useEffect(() => {
    mountedRef.current = true;
    const visibility = () => {
      if (document.visibilityState === 'hidden') abortActive();
      else if (targetRef.current && enabledRef.current) startTarget(targetRef.current);
    };
    document.addEventListener('visibilitychange', visibility);
    return () => { mountedRef.current = false; cancelPermission(); document.removeEventListener('visibilitychange', visibility); enabledRef.current = false; targetRef.current = null; transcriptRef.current = null; abortActive(); };
  }, [abortActive, cancelPermission, startTarget]);

  return { ...state, enable, disable, listen, stop };
}
